/**
 * Trade-history helpers for landing / overview / Analytics.
 * Source: `GET /api/trade-history` (cookie session + optional wallet).
 */

import { oroborosJson, oroborosAuthHeaders } from './client';
import { formatSignedUsd, formatUsd } from './portfolio';
import { fillTimestampMs, type OroTradeWindowStats } from './tradeWindows';

export type { OroRealizedWindowKpi, OroTradeWindowStats } from './tradeWindows';
export { computeTradeWindowStats, fillTimestampMs, realizedWindowKpis } from './tradeWindows';

export type OroTradeFill = {
  time: number;
  closedPnl: number;
  fee?: number;
  px?: number;
  sz?: number;
  notional?: number;
  coin?: string;
  side?: string;
  /** HL fill direction when present (`Open Long`, `Close Short`, …). */
  dir?: string;
};

export type OroTradePairStat = {
  pair: string;
  volume: number;
  trades: number;
  pnl: number;
};

export type OroTradeStats = {
  totalTrades: number;
  totalVolume: number;
  totalFees: number;
  totalPnL: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  avgTradeSize: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  topPairs: OroTradePairStat[];
  tradingPeriod?: {
    startDate: string;
    endDate: string;
    days: number;
  };
};

export type OroEquityPoint = {
  t: number;
  equity: number;
};

export type OroAnalyticsDays = 7 | 30 | 90;

export const ORO_ANALYTICS_DAYS: OroAnalyticsDays[] = [7, 30, 90];

function fillNotional(t: any): number {
  const px = Number(t?.px ?? t?.price ?? 0);
  const sz = Number(t?.sz ?? t?.size ?? 0);
  if (Number.isFinite(px) && Number.isFinite(sz)) return Math.abs(px * sz);
  const n = Number(t?.notional ?? t?.ntl ?? 0);
  return Number.isFinite(n) ? Math.abs(n) : 0;
}

function mapFill(t: any): OroTradeFill {
  return {
    time: Number(t?.time || t?.timestamp || 0) || 0,
    closedPnl: Number(t?.closedPnl ?? t?.pnl ?? 0) || 0,
    fee: Number(t?.fee ?? 0) || 0,
    px: Number(t?.px ?? t?.price ?? 0) || undefined,
    sz: Number(t?.sz ?? t?.size ?? 0) || undefined,
    notional: fillNotional(t),
    coin: t?.coin ? String(t.coin) : undefined,
    side: t?.side ? String(t.side) : undefined,
    dir: t?.dir ? String(t.dir) : undefined,
  };
}

function mapStats(raw: any): OroTradeStats | null {
  if (!raw || typeof raw !== 'object') return null;
  const topPairs = Array.isArray(raw.topPairs)
    ? raw.topPairs.map((p: any) => ({
        pair: String(p?.pair || p?.coin || '—'),
        volume: Number(p?.volume || 0) || 0,
        trades: Number(p?.trades || 0) || 0,
        pnl: Number(p?.pnl || 0) || 0,
      }))
    : [];
  return {
    totalTrades: Number(raw.totalTrades || 0) || 0,
    totalVolume: Number(raw.totalVolume || 0) || 0,
    totalFees: Number(raw.totalFees || 0) || 0,
    totalPnL: Number(raw.totalPnL || 0) || 0,
    winningTrades: Number(raw.winningTrades || 0) || 0,
    losingTrades: Number(raw.losingTrades || 0) || 0,
    winRate: Number(raw.winRate || 0) || 0,
    avgTradeSize: Number(raw.avgTradeSize || 0) || 0,
    avgWin: Number(raw.avgWin || 0) || 0,
    avgLoss: Number(raw.avgLoss || 0) || 0,
    profitFactor: Number(raw.profitFactor || 0) || 0,
    topPairs,
    tradingPeriod: raw.tradingPeriod
      ? {
          startDate: String(raw.tradingPeriod.startDate || ''),
          endDate: String(raw.tradingPeriod.endDate || ''),
          days: Number(raw.tradingPeriod.days || 0) || 0,
        }
      : undefined,
  };
}

function walletQuery(): string {
  try {
    const wallet =
      (window as any)?.JesterApp?.userWallet || localStorage.getItem('jester_user_wallet') || '';
    if (/^0x[a-fA-F0-9]{40}$/.test(wallet)) return wallet;
  } catch {
    /* ignore */
  }
  return '';
}

export async function fetchOroborosTradeHistory(days = 90): Promise<{
  success: boolean;
  trades: OroTradeFill[];
  stats: OroTradeStats | null;
  error?: string;
  unauthorized?: boolean;
}> {
  const qs = new URLSearchParams({ days: String(days) });
  const wallet = walletQuery();
  if (wallet) qs.set('wallet', wallet);

  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    trades?: any[];
    stats?: any;
    error?: string;
    message?: string;
  }>(`/api/trade-history?${qs}`, { headers: oroborosAuthHeaders() });

  if (status === 401 || status === 403) {
    return { success: false, trades: [], stats: null, unauthorized: true, error: 'Unauthorized' };
  }
  if (!ok || !data?.success) {
    return {
      success: false,
      trades: [],
      stats: null,
      error: data?.error || data?.message || 'Trade history failed',
    };
  }
  const trades: OroTradeFill[] = (Array.isArray(data.trades) ? data.trades : []).map(mapFill);
  return { success: true, trades, stats: mapStats(data.stats) };
}

/** Cumulative realized PnL from fills that closed a position (closedPnl ≠ 0). */
export function buildEquityCurve(
  trades: OroTradeFill[],
  maxPoints = 96,
  maxDays?: number,
): OroEquityPoint[] {
  const now = Date.now();
  const cutoffMs =
    maxDays != null && Number.isFinite(maxDays) && maxDays > 0
      ? now - maxDays * 86_400_000
      : 0;
  const closing = trades
    .filter((t) => {
      if (!Number.isFinite(t.closedPnl) || t.closedPnl === 0 || t.time <= 0) return false;
      if (cutoffMs > 0 && fillTimestampMs(t) < cutoffMs) return false;
      return true;
    })
    .slice()
    .sort((a, b) => fillTimestampMs(a) - fillTimestampMs(b));
  if (closing.length === 0) return [];

  let equity = 0;
  const raw: OroEquityPoint[] = closing.map((t) => {
    equity += t.closedPnl;
    return { t: fillTimestampMs(t), equity };
  });

  if (raw.length <= maxPoints) return raw;
  const step = (raw.length - 1) / (maxPoints - 1);
  const out: OroEquityPoint[] = [];
  for (let i = 0; i < maxPoints; i++) {
    const idx = Math.round(i * step);
    out.push(raw[Math.min(raw.length - 1, idx)]);
  }
  return out;
}

/** Recent closing fills for the activity table (newest first). */
export function recentClosingFills(trades: OroTradeFill[], limit = 16): OroTradeFill[] {
  return trades
    .filter((t) => Number.isFinite(t.closedPnl) && t.closedPnl !== 0)
    .slice()
    .sort((a, b) => fillTimestampMs(b) - fillTimestampMs(a))
    .slice(0, limit);
}

/** Map server-proxied Propr venue fills → Oroboros trade table shape. */
export function mapProprVenueFill(f: {
  timestamp?: number;
  closedPnl?: number;
  fee?: number;
  price?: number;
  size?: number;
  pair?: string;
  side?: string;
}): OroTradeFill {
  const pair = String(f.pair || '');
  const coin = pair.includes('-') ? pair.split('-')[0] : pair;
  return {
    time: Number(f.timestamp || 0) || 0,
    closedPnl: Number(f.closedPnl || 0) || 0,
    fee: Number(f.fee || 0) || 0,
    px: Number(f.price || 0) || undefined,
    sz: Number(f.size || 0) || undefined,
    coin: coin || undefined,
    side: f.side ? String(f.side) : undefined,
  };
}

export async function fetchOroborosProprTradeHistory(days = 30): Promise<{
  success: boolean;
  trades: OroTradeFill[];
  error?: string;
  unauthorized?: boolean;
}> {
  const qs = new URLSearchParams({ days: String(days) });
  const { ok, status, data } = await oroborosJson<{
    success?: boolean;
    fills?: any[];
    error?: string;
  }>(`/api/terminal/trades/propr?${qs}`, { headers: oroborosAuthHeaders() });

  if (status === 401 || status === 403) {
    return { success: false, trades: [], unauthorized: true, error: 'Unauthorized' };
  }
  if (!ok || !data?.success) {
    return {
      success: false,
      trades: [],
      error: data?.error || `Propr trade history failed (${status})`,
    };
  }
  const trades = (Array.isArray(data.fills) ? data.fills : []).map(mapProprVenueFill);
  return { success: true, trades };
}

export function formatLandingStats(opts: {
  equity?: number;
  unrealized?: number;
  windows?: OroTradeWindowStats | null;
}): { value: string; label: string; positive?: boolean }[] {
  const equity = opts.equity ?? 0;
  const unrealized = opts.unrealized ?? 0;
  const w = opts.windows;
  return [
    { value: formatUsd(equity), label: 'Account Equity' },
    {
      value: w ? formatSignedUsd(w.pnl24h) : '—',
      label: '24H Realized PnL',
      positive: w ? w.pnl24h >= 0 : undefined,
    },
    {
      value: w ? formatSignedUsd(w.pnl7d) : '—',
      label: '7D Realized PnL',
      positive: w ? w.pnl7d >= 0 : undefined,
    },
    {
      value: formatSignedUsd(unrealized),
      label: 'Open Unrealized',
      positive: unrealized >= 0,
    },
    {
      value: w ? formatUsd(w.volume30d, 0) : '—',
      label: '30D Volume',
    },
  ];
}
